import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Menu } from "lucide-react";
import { useState, useEffect, lazy, Suspense } from "react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";

// These pull in Radix (dialog/dropdown-menu) which is heavy. They are only
// needed on interaction, so we lazy-load them to keep the initial homepage
// bundle free of Radix — the single biggest mobile performance win.
const DesktopNav = lazy(() =>
  import("@/components/DesktopNav").then((m) => ({ default: m.DesktopNav })),
);
const MobileMenu = lazy(() =>
  import("@/components/MobileMenu").then((m) => ({ default: m.MobileMenu })),
);
const LoginModal = lazy(() =>
  import("@/components/LoginModal").then((m) => ({ default: m.LoginModal })),
);

export function Navbar() {
  const { isAuthenticated, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 20);
        ticking = false;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      {/* Topbar — navy strip */}
      <div className="bg-primary text-primary-foreground/85 text-center text-[10px] tracking-[0.24em] uppercase py-2.5 px-6 hidden md:block">
        Independent perspective · Coordinated financial strategy · Serious
        business-owner decisions
      </div>

      {/* Header — solid ivory */}
      <div
        className={cn(
          "bg-background border-b border-border transition-all duration-500",
          isScrolled
            ? "py-2.5 shadow-[0_8px_30px_-12px_rgba(14,30,60,0.25)]"
            : "py-4",
        )}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center w-[85%] sm:w-auto">
              <Link to="/" className="flex items-center gap-3 group relative">
                <div className="flex flex-col">
                  <div className="flex items-center h-7 sm:h-9 xl:h-11 relative">
                    {/* Page load gold seal glow animation: fires on initial mount to reinforce brand "stand out" moment */}
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute -inset-x-3 -inset-y-2 rounded-xl animate-logo-load-glow bg-accent/[0.08]"
                    />
                    {/* Hover seal glow micro-interaction */}
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute -inset-x-2 -inset-y-1.5 rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:shadow-[0_0_24px_-3px_hsl(var(--accent)/0.6)] group-hover:bg-accent/[0.06]"
                    />
                    <Logo
                      size="md"
                      className="relative transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  <span className="relative text-[10px] xl:text-[9px] uppercase tracking-widest sm:tracking-[0.15em] text-accent font-bold mt-1 opacity-90 leading-tight whitespace-nowrap hidden xl:block">
                    Business Strategy • Wealth Preservation • Capital Solutions
                    {/* Gold underline grows in on hover */}
                    <span className="absolute -bottom-1 left-0 h-px w-0 group-hover:w-full bg-accent transition-all duration-500" />
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-accent font-bold mt-1 opacity-90 leading-tight sm:block xl:hidden">
                    License No. 19177286
                  </span>
                </div>
              </Link>
            </div>

            {/* Desktop Nav — lazy so Radix dropdowns don't ship on mobile */}
            <Suspense fallback={null}>
              <DesktopNav
                isAuthenticated={isAuthenticated}
                setShowLoginModal={setShowLoginModal}
                logout={logout}
              />
            </Suspense>

            {/* Mobile Menu Button */}
            <div className="xl:hidden flex items-center">
              <Button
                variant="ghost"
                size="icon"
                className="text-primary"
                onClick={() => setIsOpen(true)}
                aria-label="Open menu"
              >
                <Menu className="h-7 w-7" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu — lazy so Radix dialog stays out of the initial bundle */}
      <Suspense fallback={null}>
        <MobileMenu
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onOpenLogin={() => setShowLoginModal(true)}
        />
      </Suspense>

      {/* Login modal — lazy, only mounts when opened */}
      <Suspense fallback={null}>
        {showLoginModal && (
          <LoginModal
            isOpen={showLoginModal}
            onClose={() => setShowLoginModal(false)}
          />
        )}
      </Suspense>
    </nav>
  );
}
